import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {connect} from 'react-redux';
import MessageType from '../../enums/MessageType';
import Status from '../../enums/Status';
import DeliveryOptionPropType from '../../propTypes/DeliveryOption.proptype';
import ScreenReaderOnly from '../../UI/ScreenReaderOnly/ScreenReaderOnly';
import {updateDeliveryContactNumber} from '../../v2/actions/address-actions';
import {getDeliveryOptions} from '../../v2/actions/deliveryOptionActions';
import ContactNumber from '../../v2/components/address/ContactNumber/ContactNumber';
import ResidentIDLazyLoader from '../../v2/components/ImportTax/ResidentIDForm/ResidentIDLazyLoader';
import Message from '../../v2/components/ui/Message/Message';
import Spinner from '../../v2/components/ui/Spinner';
import FieldContainer2 from '../../v2/form/FieldContainer2';
import Translation from '../../v2/lang/Translation';
import {isResidentIDRequiredInAddressForm} from '../../v2/selectors/DeliveryAddressSelectors/DeliveryAddressSelectors';
import {
  getDeliveryGroups,
  hasDeliveryOptions,
} from '../../v2/selectors/DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import getPONDErrorMessage from '../../v2/selectors/DeliveryOptionsSelectors/getPONDErrorMessage';
import getIsUsingExpressCheckout from '../../v2/selectors/ExpressSelectors/getIsUsingExpressCheckout';
import makeGetDeliverySpecificKey from '../../v2/selectors/makeGetDeliverySpecificKey';
import {getSiteCode, getSettings} from '../../v2/selectors/selectors';
import replaceHTMLEntityWithSymbol from '../../v2/utils/replace-html-entity-with-symbol';
import V2Components from '../../v2/v2-components';
import CollapsedDeliveryOptionSection from './CollapsedDeliveryOptionSection';
import ConnectionLost from './ConnectionLost/ConnectionLost';
import DeliveryOptionBodyLazyLoader from './DeliveryOptionBody/DeliveryOptionBodyLazyLoader';
import DeliveryOptionDelayMessage from './DeliveryOptionDelayMessage/DeliveryOptionDelayMessage';
import OptionalDeliveryBasketItem from './OptionalDeliveryBasketItem/OptionalDeliveryBasketItem';
import './DeliveryOptionSection.scss';

export function DeliveryOptionSection(props) {
  const [hasValidationTriggered, setHasValidationTriggered] = useState(false);
  const prevStatusRef = useRef();
  const prevValidationTriggeredRef = useRef();
  const fieldContainerRef = useRef(null);
  
  const validate = useCallback(
    () => {
      const getInvalidResponse = (errorCode, pageError) => ({
        isValid: false,
        errors: [{
          fieldName: 'deliveryOptions',
          errorCode,
          pageError,
        }],
      });
      
      if (fieldContainerRef.current && props.usingExpressCheckout) {
        const containerValidationResult = fieldContainerRef.current.validate();
        
        if (!containerValidationResult.isValid && containerValidationResult.errors.length > 0) {
          const error = containerValidationResult.errors[0];
          return getInvalidResponse(
            error.errorCode,
            error.fieldName,
          );
        }
      }
      
      if (props.deliveryOption.status === Status.NETWORK_ERROR) {
        return getInvalidResponse(
          'error.deliveryOptionsNetworkError',
          'error.deliveryOptionsNetworkError',
        );
      }
      
      if (props.deliveryOption.status !== 'success') {
        // Edge autofill bug. It's possible for postcode to have been entered without delivery options triggered. In this
        // rare case, try loading delivery options again. getDeliveryOptions has built-in postcode validation
        props.getDeliveryOptions();
        return getInvalidResponse(
          'error.deliveryOptionsNotLoaded',
          props.requireDataMessage,
        );
      }
      
      if (props.isBlackListedRegion) {
        return getInvalidResponse(
          'error.deliveryRegionBlacklisted',
          'errors.blacklistedRegion',
        );
      }
      
      if (props.isInactiveCollectionPoint) {
        return getInvalidResponse(
          'error.deliveryRegionInactiveCollectPoint',
          'errors.inactiveClickAndCollectLocation',
        );
      }
      
      if (!props.hasDeliveryOptions && !props.hasUnshippableGroups) {
        return getInvalidResponse(
          'error.deliveryOptionsMissingDeliveryOptions',
          'deliveryOptions.error',
        );
      }
      
      setHasValidationTriggered(true);
      
      const hasDeliveryGroupWithoutSelection = props.deliveryOption.deliveryGroups?.some(
        deliveryGroup => deliveryGroup.selectedOptionIndex < 0,
      );
      
      if (hasDeliveryGroupWithoutSelection) {
        return getInvalidResponse(
          'error.noSelectedDeliveryOption',
          'errors.formFields.deliveryOptions',
        );
      }
      
      return {isValid: true};
    }, [props, fieldContainerRef],
  );
  
  useEffect(() => {
    V2Components.register('DeliveryOptionSection', {validate});
    return () => V2Components.clear('DeliveryOptionSection');
  }, [validate]);
  
  useEffect(() => {
    prevValidationTriggeredRef.current = hasValidationTriggered;
  }, [hasValidationTriggered]);
  
  useEffect(() => {
    prevStatusRef.current = props.deliveryOption.status;
  }, [props.deliveryOption.status]);
  
  useEffect(() => {
    // If deliveryOption no longer 'success' (ie they've reloaded their delivery options) but it previously was
    // then don't initially display errors
    if (props.deliveryOption.status !== 'success'
      && prevStatusRef.current === 'success'
      && prevValidationTriggeredRef.current === false) {
      setHasValidationTriggered(false);
    }
  }, [prevValidationTriggeredRef, prevStatusRef, props.deliveryOption.status]);
  
  const renderDeliverySection = (messageType, textMessage, hideIcon = false) => (
    <div className={`DeliveryOptionSection-body--${props.deliveryOption.status}`}>
      <Message type={messageType} hideIcon={hideIcon}>
        <Translation optionalExtension={props.siteCode}>{textMessage}</Translation>
      </Message>
    </div>
  );
  
  const renderDeliverySectionPONDNotAvailable = (messageType, textMessage, hideIcon = false) => {
    const value = props.hasPONDError.metadata ? replaceHTMLEntityWithSymbol(props.hasPONDError.metadata.AMOUNT) : null;
    if (value) {
      return (
        <Message type={messageType} hideIcon={hideIcon}>
          <Translation placeholderValues={[value]} optionalExtension={props.siteCode}>{textMessage}</Translation>
        </Message>
      );
    }
    return (
      <Message type={messageType} hideIcon={hideIcon}>
        <Translation optionalExtension={props.siteCode}>{textMessage}</Translation>
      </Message>
    );
  };
  
  const getDeliveryOptionSuccess = () => {
    if (props.isBlackListedRegion) {
      return renderDeliverySection(MessageType.ERROR, 'errors.blacklistedRegion');
    }
    
    if (props.isInactiveCollectionPoint) {
      return renderDeliverySection(MessageType.ERROR, 'errors.inactiveClickAndCollectLocation');
    }
    
    if (!props.hasDeliveryOptions && !props.hasUnshippableGroups) {
      return renderDeliverySection(MessageType.ERROR, 'deliveryOptions.error');
    }
    
    return (
      <div className="DeliveryOptionSection-body--success">
        {
          props.hasPONDError && renderDeliverySectionPONDNotAvailable(MessageType.INFO, `deliveryOptions.error.pond.${props.hasPONDError.messageReason}`)
        }
        <DeliveryOptionBodyLazyLoader
          deliveryGroups={props.deliveryOption.deliveryGroups}
          hasValidationTriggered={hasValidationTriggered}
        />
      </div>
    );
  };
  
  const getDeliveryOptionBody = () => {
    switch (props.deliveryOption.status) {
      case 'loading':
        return (
          <div className="DeliveryOptionSection-body--loading">
            <Spinner size="small" />
            <p className="DeliveryOptionSection-body--loading-text">
              <Translation>deliveryOptions.loading</Translation>
            </p>
          </div>
        );
      case 'requireData':
        return renderDeliverySection(MessageType.INFO, props.requireDataMessage);
      case 'success':
        return (
          getDeliveryOptionSuccess()
        );
      case Status.NETWORK_ERROR:
        return <ConnectionLost onClick={props.getDeliveryOptions} />;
      default:
        return renderDeliverySection(MessageType.ERROR, 'deliveryOptions.error');
    }
  };
  
  return (
    <>
      {props.enableEditableBasket && <OptionalDeliveryBasketItem />}
      {props.getDeliveryGroupsSize > 1 && (
        <ScreenReaderOnly>
          <Translation placeholderValues={[props.getDeliveryGroupsSize]}>
            accessibility.split.basket.text
          </Translation>
        </ScreenReaderOnly>
      )}
      <DeliveryOptionDelayMessage />
      <div className="DeliveryOptionSection__contents DeliveryOptionSection-body">
        {props.isStepCollapsed ? (
          <CollapsedDeliveryOptionSection />
        ) : (
          getDeliveryOptionBody()
        )}
      </div>
      {props.usingExpressCheckout && (
        <>
          <FieldContainer2>
            {(fieldContainer) => {
              fieldContainerRef.current = fieldContainer;
              return (
                <ContactNumber
                  fieldContainer={fieldContainer.fieldUpdateDispatcher(props.updateContactNumberDispatcher)}
                />
              );
            }}
          </FieldContainer2>
          {props.shouldShowResidentIDForm && <ResidentIDLazyLoader />}
        </>
      )}
    </>
  );
}

DeliveryOptionSection.propTypes = {
  deliveryOption: DeliveryOptionPropType.isRequired,
  getDeliveryOptions: PropTypes.func.isRequired,
  updateContactNumberDispatcher: PropTypes.func.isRequired,
  isBlackListedRegion: PropTypes.bool.isRequired,
  isInactiveCollectionPoint: PropTypes.bool.isRequired,
  hasDeliveryOptions: PropTypes.bool.isRequired,
  hasUnshippableGroups: PropTypes.bool.isRequired,
  getDeliveryGroupsSize: PropTypes.number.isRequired,
  isStepCollapsed: PropTypes.bool.isRequired,
  usingExpressCheckout: PropTypes.bool.isRequired,
  shouldShowResidentIDForm: PropTypes.bool.isRequired,
  enableEditableBasket: PropTypes.bool.isRequired,
  siteCode: PropTypes.string,
  requireDataMessage: PropTypes.string,
  hasPONDError: {
    messageReason: PropTypes.string,
    metadata: {
      AMOUNT: PropTypes.string,
      PRODUCT_IDS: PropTypes.arrayOf(PropTypes.string),
    },
  },
};

const mapStateToProps = state => ({
  deliveryOption: state.deliveryOption,
  isBlackListedRegion: state.deliveryOption.blacklistedRegion,
  isInactiveCollectionPoint: state.deliveryOption.clickAndCollectLocationInactive,
  hasUnshippableGroups: state.deliveryOption.unshippableGroups.length > 0,
  hasDeliveryOptions: hasDeliveryOptions(state),
  getDeliveryGroupsSize: getDeliveryGroups(state).length,
  usingExpressCheckout: getIsUsingExpressCheckout(state),
  shouldShowResidentIDForm: isResidentIDRequiredInAddressForm(state),
  enableEditableBasket: !!getSettings(state)?.enable_editable_basket,
  siteCode: getSiteCode(state),
  requireDataMessage: makeGetDeliverySpecificKey('deliveryOptions.requireData')(state),
  hasPONDError: getPONDErrorMessage(state),
});

const mapDispatchToProps = dispatch => ({
  updateContactNumberDispatcher: (_, value) => {
    dispatch(updateDeliveryContactNumber(value));
  },
  getDeliveryOptions: () => dispatch(getDeliveryOptions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryOptionSection);
